(() => {
  if (customElements.get('decanter-search')) return;
  class DecanterSearch extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.input = this.querySelector('input[name="q"]');
      this.results = this.querySelector('[data-search-results]');
      this.discovery = this.querySelector('[data-search-discovery]');
      this.status = this.querySelector('[data-search-status]');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.allButton = this.querySelector('[data-search-all]');
      this.form = this.querySelector('form');
      this.cache = new Map();
      this.revision = 0;
      this.activeIndex = -1;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.input.addEventListener('input', () => this.schedule(), options);
      this.input.addEventListener('focus', () => {
        if (!this.suppressFocus && this.input.value.trim() && this.results.hidden) this.schedule();
      }, options);
      this.input.addEventListener('compositionstart', () => { this.composing = true; }, options);
      this.input.addEventListener('compositionend', () => { this.composing = false; this.schedule(); }, options);
      this.addEventListener('keydown', event => this.onKeydown(event), options);
      this.clearButton.addEventListener('click', () => {
        this.input.value = '';
        this.schedule();
        this.input.focus();
      }, options);
      this.addEventListener('click', event => {
        const shortcut = event.target.closest('[data-search-shortcut]');
        if (!shortcut || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
        if (this.dataset.predictive !== 'true') return;
        event.preventDefault();
        this.input.value = shortcut.dataset.searchShortcut;
        this.schedule();
        this.input.focus();
      }, options);
      this.form.addEventListener('submit', event => {
        if (!this.input.value.trim()) { event.preventDefault(); this.input.focus(); }
      }, options);
      this.drawer = this.closest('.decanter-search-drawer');
      if (!this.drawer) {
        this.addEventListener('focusout', event => {
          if (this.contains(event.relatedTarget)) return;
          clearTimeout(this.timer);
          this.request?.abort();
          this.revision += 1;
          this.results.hidden = true;
          this.input.setAttribute('aria-expanded', 'false');
          this.input.removeAttribute('aria-activedescendant');
          this.status.textContent = '';
        }, options);
      }
      if (this.drawer) {
        this.drawer.addEventListener('toggle', event => this.onToggle(event), options);
        this.drawer.addEventListener('keydown', event => this.trapFocus(event), options);
        document.addEventListener('click', event => {
          const trigger = event.target.closest('[popovertarget="' + this.drawer.id + '"]');
          if (trigger && trigger.getAttribute('popovertargetaction') !== 'hide') this.opener = trigger;
        }, options);
      }
      this.updateControls();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      clearTimeout(this.timer);
      this.restoreBackground();
    }

    updateControls() {
      const hasQuery = Boolean(this.input.value.trim());
      this.clearButton.hidden = !hasQuery;
      this.allButton.hidden = !hasQuery;
      if (this.discovery) this.discovery.hidden = hasQuery && !this.results.hidden && Number(this.results.firstElementChild?.dataset.searchCount) > 0;
    }

    schedule() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.revision += 1;
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
      this.status.textContent = '';
      this.status.classList.remove('visually-hidden');
      this.updateControls();
      const query = this.input.value.trim();
      if (!query || this.composing || this.dataset.predictive !== 'true') return;
      if (this.discovery) this.discovery.hidden = true;
      this.status.textContent = this.dataset.loading;
      this.timer = setTimeout(() => this.search(query, this.revision), 200);
    }

    async search(query, revision) {
      try {
        if (this.cache.has(query)) { this.render(this.cache.get(query), revision); return; }
        const request = new AbortController();
        this.request = request;
        const timeout = setTimeout(() => request.abort(), 8000);
        let text;
        try {
          const url = new URL(this.dataset.url, window.location.origin);
          url.searchParams.set('q', query);
          url.searchParams.set('section_id', 'predictive-search');
          url.searchParams.set('resources[limit]', '8');
          url.searchParams.set('resources[limit_scope]', 'each');
          if (this.dataset.productsOnly === 'true') url.searchParams.set('resources[type]', 'product,collection');
          const response = await fetch(url, { signal: request.signal });
          if (!response.ok) throw new Error('Search unavailable');
          text = await response.text();
        } finally { clearTimeout(timeout); }
        if (revision !== this.revision) return;
        const responseDocument = new DOMParser().parseFromString(text, 'text/html');
        const content = responseDocument.querySelector('#predictive-search-results');
        if (!content) throw new Error('Missing search results');
        this.cache.set(query, content.outerHTML);
        if (this.cache.size > 20) this.cache.delete(this.cache.keys().next().value);
        this.render(content.outerHTML, revision);
      } catch (error) {
        if (revision !== this.revision) return;
        this.status.classList.remove('visually-hidden');
        this.status.textContent = this.dataset.error;
        this.results.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
        if (this.discovery) this.discovery.hidden = false;
      }
    }

    render(markup, revision) {
      if (revision !== this.revision) return;
      this.results.innerHTML = markup;
      const idMap = new Map();
      this.results.querySelectorAll('[id]').forEach(element => {
        const newId = this.input.id + '-' + element.id;
        idMap.set(element.id, newId);
        element.id = newId;
      });
      this.results.querySelectorAll('[aria-labelledby]').forEach(element => {
        element.setAttribute('aria-labelledby', element.getAttribute('aria-labelledby').split(' ').map(id => idMap.get(id) || id).join(' '));
      });
      const list = this.results.querySelector('[role="listbox"]');
      if (list) list.id = this.input.id + '-list';
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', String(Boolean(list)));
      this.status.classList.add('visually-hidden');
      this.status.textContent = this.results.firstElementChild.dataset.searchMessage;
      this.updateControls();
    }

    onKeydown(event) {
      if (event.isComposing || this.composing) return;
      if (event.key === 'Escape' && !this.results.hidden) {
        event.preventDefault();
        event.stopPropagation();
        clearTimeout(this.timer);
        this.request?.abort();
        this.revision += 1;
        this.results.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
        this.status.textContent = '';
        this.updateControls();
        this.suppressFocus = true;
        this.input.focus();
        this.suppressFocus = false;
        return;
      }
      if (event.key === 'Escape' && this.drawer) {
        event.preventDefault();
        this.drawer.hidePopover();
        return;
      }
      if (event.target !== this.input || this.results.hidden) return;
      const options = [...this.results.querySelectorAll('[data-search-option]')];
      if (!options.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = this.activeIndex < 0 ? (step === 1 ? 0 : options.length - 1) : (this.activeIndex + step + options.length) % options.length;
        options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.activeIndex)));
        const active = options[this.activeIndex];
        this.input.setAttribute('aria-activedescendant', active.id);
        const scroller = this.drawer?.querySelector('.decanter-search-drawer__body') || this.results;
        const bounds = scroller.getBoundingClientRect();
        const target = active.getBoundingClientRect();
        if (target.bottom > bounds.bottom) scroller.scrollTop += target.bottom - bounds.bottom;
        else if (target.top < bounds.top) scroller.scrollTop -= bounds.top - target.top;
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        options[this.activeIndex]?.querySelector('a')?.click();
      }
    }

    restoreBackground() {
      if (!this.inertElements) return;
      this.inertElements.forEach(element => { element.inert = false; });
      this.inertElements = null;
      document.documentElement.classList.remove('search-drawer-open');
    }

    onToggle(event) {
      const open = event.newState === 'open';
      document.querySelectorAll('[popovertarget="' + this.drawer.id + '"]').forEach(trigger => {
        if (trigger.getAttribute('popovertargetaction') !== 'hide') trigger.setAttribute('aria-expanded', String(open));
      });
      if (open) {
        this.inertElements = [];
        for (let branch = this.drawer; branch.parentElement && branch !== document.body; branch = branch.parentElement) {
          [...branch.parentElement.children].forEach(sibling => {
            if (sibling !== branch && !sibling.inert && !['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) {
              sibling.inert = true;
              this.inertElements.push(sibling);
            }
          });
        }
        document.documentElement.classList.add('search-drawer-open');
        this.input.focus({ preventScroll: true });
        this.schedule();
      } else {
        this.request?.abort();
        clearTimeout(this.timer);
        this.revision += 1;
        this.restoreBackground();
        this.opener?.focus({ preventScroll: true });
      }
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const elements = [...this.drawer.querySelectorAll('a[href], button, input:not([type="hidden"])')]
        .filter(element => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length);
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  }
  customElements.define('decanter-search', DecanterSearch);
  document.addEventListener('toggle', event => {
    if (event.target.matches?.('.search-facets__filter[open]')) {
      event.target.closest('form').querySelectorAll('.search-facets__filter[open]').forEach(filter => {
        if (filter !== event.target) filter.open = false;
      });
    }
  }, true);
  document.addEventListener('click', event => {
    document.querySelectorAll('.search-facets__filter[open]').forEach(filter => {
      if (!filter.contains(event.target)) filter.open = false;
    });
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const filter = event.target.closest('.search-facets__filter[open]');
    if (filter) { filter.open = false; filter.querySelector('summary').focus(); }
  });
})();
